import {
  parseMatchRoomFromLocation,
  parseRosterJoinFromLocation,
} from "../services/session/matchShare";
import {
  readPendingRosterInviteRoom,
  writePendingRosterInviteRoom,
} from "../domain/auth/rosterJoinSession";
import { readPendingProfileOnboarding } from "../domain/auth/profileOnboardingSession";
import {
  clearAuthReturnLocation,
  formatAuthReturnTarget,
  normalizeAuthReturnLocation,
  readAuthReturnLocation,
  writeAuthReturnLocation,
} from "../domain/auth/authReturnLocation";
import { normalizeMatchRoomCode } from "../utils/matchRoomCode";
import {
  hasPendingArenaInvitation,
  readPendingArenaInvitation,
  writePendingArenaInvitation,
} from "../domain/arenaInvitation/pendingArenaInvitation";
import {
  captureTournamentRegistrationFromLocation,
  clearPendingTournamentRegistration,
  hasPendingTournamentRegistration,
  isTournamentRegisterPath,
  readPendingTournamentRegistration,
  writePendingTournamentRegistration,
} from "../domain/auth/pendingTournamentRegistration";

/** Persist room code from a roster invite deep link (sign-in return URL). */
export function captureRosterInviteFromLocation(fromLocation) {
  if (!fromLocation || !parseRosterJoinFromLocation(fromLocation)) return "";
  const room = parseMatchRoomFromLocation(fromLocation);
  if (room) writePendingRosterInviteRoom(room);
  return room;
}

/**
 * Capture /join/:invitationId into session pending so auth never dumps to Home.
 * @param {Pick<Location, "pathname" | "search" | "hash"> | undefined} fromLocation
 */
export function captureArenaInviteFromLocation(fromLocation) {
  const pathname = fromLocation?.pathname || "";
  const match = pathname.match(/^\/join\/([^/]+)/);
  if (!match) return null;
  const invitationId = decodeURIComponent(match[1] || "").trim();
  if (!invitationId) return null;
  return writePendingArenaInvitation({
    invitationId,
    targetRoute: `/join/${encodeURIComponent(invitationId)}`,
  });
}

export function hasPendingRosterInvite(fromLocation) {
  if (readPendingRosterInviteRoom()) return true;
  if (fromLocation && parseRosterJoinFromLocation(fromLocation)) return true;
  return false;
}

export function shouldSkipProfileOnboardingForArenaInvite(fromLocation) {
  if (hasPendingArenaInvitation()) return true;
  const pathname = fromLocation?.pathname || "";
  return /^\/join\//.test(pathname);
}

export function shouldSkipProfileOnboardingForTournamentRegister(fromLocation) {
  if (hasPendingTournamentRegistration()) return true;
  return isTournamentRegisterPath(fromLocation);
}

/**
 * New accounts skip forced Me edit when joining via roster QR or arena invite.
 * @param {Pick<Location, "pathname" | "search"> | undefined} fromLocation
 */
export function shouldSkipProfileOnboardingForRosterInvite(fromLocation) {
  return (
    hasPendingRosterInvite(fromLocation) ||
    shouldSkipProfileOnboardingForArenaInvite(fromLocation) ||
    shouldSkipProfileOnboardingForTournamentRegister(fromLocation)
  );
}

function isNeutralReturnLocation(loc) {
  if (!loc) return true;
  const pathname = loc.pathname || "/";
  return pathname === "/" || pathname === "" || pathname === "/sign-in";
}

/**
 * Resolve the best post-auth return target from router state, ?next=, or session memory.
 * @param {Pick<Location, "pathname" | "search" | "hash"> | undefined} fromLocation
 * @param {string | undefined} nextParam
 */
export function resolvePostAuthReturnLocation(fromLocation, nextParam) {
  const fromState = normalizeAuthReturnLocation(fromLocation);
  if (fromState) {
    writeAuthReturnLocation(fromState);
    return fromState;
  }
  const fromNext = normalizeAuthReturnLocation(nextParam);
  if (fromNext) {
    writeAuthReturnLocation(fromNext);
    return fromNext;
  }
  return readAuthReturnLocation();
}

/**
 * Where to send the user after WelcomePage auth succeeds.
 * Prefer the page they came from so a refresh/auth bounce never dumps them on Home.
 * New RallyAlly registrations (no roster invite, no meaningful return) open Me in edit mode.
 *
 * @param {boolean} pendingProfileOnboarding
 * @param {Pick<Location, "pathname" | "search" | "hash"> | undefined} fromLocation
 * @param {string | undefined} nextParam safe relative path from `?next=`
 * @returns {string | { pathname: string; search?: string; hash?: string }}
 */
export function postSignInRedirectPath(
  pendingProfileOnboarding,
  fromLocation,
  nextParam
) {
  const returnLoc = resolvePostAuthReturnLocation(fromLocation, nextParam);
  captureRosterInviteFromLocation(returnLoc || fromLocation);
  captureArenaInviteFromLocation(returnLoc || fromLocation);
  captureTournamentRegistrationFromLocation(returnLoc || fromLocation);
  if (nextParam) captureTournamentRegistrationFromLocation(nextParam);

  // Session storage is source of truth — auth listener can authenticate before React state updates.
  const pendingEdit =
    Boolean(pendingProfileOnboarding) || readPendingProfileOnboarding();

  // Arena invitation wins over Home / profile onboarding — restore /join/:id then accept.
  const pendingArena = readPendingArenaInvitation();
  if (pendingArena?.invitationId || pendingArena?.targetRoute) {
    clearAuthReturnLocation();
    if (pendingArena.targetRoute) return pendingArena.targetRoute;
    return `/join/${encodeURIComponent(pendingArena.invitationId)}`;
  }
  if (returnLoc?.pathname && /^\/join\//.test(returnLoc.pathname)) {
    clearAuthReturnLocation();
    return formatAuthReturnTarget(returnLoc);
  }

  // Tournament self-registration — return to wizard (never divert to Me edit).
  const pendingReg = readPendingTournamentRegistration();
  if (pendingReg?.targetRoute) {
    clearAuthReturnLocation();
    clearPendingTournamentRegistration();
    return pendingReg.targetRoute;
  }
  if (returnLoc && isTournamentRegisterPath(returnLoc)) {
    clearAuthReturnLocation();
    clearPendingTournamentRegistration();
    return formatAuthReturnTarget(returnLoc);
  }

  const rosterInvite = hasPendingRosterInvite(returnLoc || fromLocation);
  const skipOnboarding = shouldSkipProfileOnboardingForRosterInvite(
    returnLoc || fromLocation
  );

  // Fresh signup only — do not yank users off an existing deep link/page.
  if (pendingEdit && !skipOnboarding && isNeutralReturnLocation(returnLoc)) {
    clearAuthReturnLocation();
    return { pathname: "/profile", search: "?edit=1" };
  }

  const pendingRoom = readPendingRosterInviteRoom();
  if (rosterInvite && pendingRoom) {
    clearAuthReturnLocation();
    return { pathname: "/play", search: rosterInviteHomeSearch(pendingRoom) };
  }

  if (!returnLoc) {
    clearAuthReturnLocation();
    return "/";
  }

  const pathname = returnLoc.pathname || "/";
  const search = returnLoc.search || "";
  const hash = returnLoc.hash || "";

  if (search.includes("room=") || search.includes("roster=")) {
    const destPath = pathname === "/" || pathname === "" ? "/play" : pathname;
    clearAuthReturnLocation();
    return formatAuthReturnTarget({ pathname: destPath, search, hash });
  }

  clearAuthReturnLocation();
  return formatAuthReturnTarget({ pathname, search, hash });
}

/**
 * Build home URL to resume a pending roster invite after profile setup.
 * @param {string} roomCode normalized
 */
export function rosterInviteHomeSearch(roomCode) {
  const code = normalizeMatchRoomCode(roomCode || "");
  if (!code) return "";
  return `?room=${encodeURIComponent(code)}&spectator=1&roster=1`;
}

export { writePendingTournamentRegistration };
